// index.js (refactored)
// Entry point that wires together Remote DB viewer, Local IndexedDB viewer, and Badge Ticker.
//
// Exposes: initSimulator()

import { EVENT_ID, OFFLINE_MODE_ENABLED } from '../core/config.js';
import { withDb } from '../data/idb.js';
import { lwDispatch, lwOn } from '../core/livewire.js';

import { createIdbReader } from './idb.js';
import { initRemote } from './remote.js';
import { initLocalViewer } from './local.js';
import { initBadgeTicker } from './ticker.js';
import { appendSimLog } from './utils.js';
import { dispatchScan, proceed } from '../features/scan.js';

export function initSimulator() {
    console.log('initSimulator');
    document.body.classList.add('checkin-sim-scan');

    // --- SIM MODE ON ---
    window.SIMULATOR_MODE = true;
    window.simulatorLog = window.simulatorLog || appendSimLog;

    // ------------------ Constants / DOM refs ------------------
    const DB_NAME = `checkin-${EVENT_ID}`;
    const STORE = 'badges';
    const UPDATED_FIELD = 'updated_at';
    const UPDATED_INDEX = 'updated_at'; // if present, native path will use it
    const POLL_MS = 1500;

    // Local UI elements
    const elements = {
        listEl: document.getElementById('idbBadgesList'),
        lastRefreshEl: document.getElementById('lastRefresh'),
        lastCheckedEl: document.getElementById('lastChecked'),
        dbStatusEl: document.getElementById('dbStatus'),
        watchStatusEl: document.getElementById('watchStatus'),
        noticeEl: document.getElementById('idbNotice'),
        spinnerEl: document.getElementById('loadingSpinner'),
        lwDispatch, // used by local viewer to invoke remote fetch
    };

    // ------------------ Modules ------------------

    // Badge ticker from template (optional; will no-op if DOM ids not present)
    const ticker = initBadgeTicker();

    // Déclenchement de scans réels pour chaque batch de badges "sélectionnés"
    window.simulatorDispatchBatch = (batch = []) => {
        if (ticker?.paused) return;
        (async () => {
            for (const { uuid, isFake } of batch) {
                try {
                    await dispatchScan({ data: uuid, type: isFake ? 'SIMULATOR_FAKE' : 'SIMULATOR' });
                } catch (e) {
                    appendSimLog({ status: 'danger', message: `dispatchScan failed: ${e?.message || e}`, uuid, type: isFake ? 'SIMULATOR_FAKE' : 'SIMULATOR' });
                }
                await new Promise(r => setTimeout(r, 150)); // séquence douce
            }
        })();
    };

    // Remote server viewer
    const remoteApi = initRemote({
        UPDATED_FIELD,
        REMOTE_LIMIT: 5,
        REMOTE_POLL_MS: 10_000,
    });

    // Local IndexedDB viewer
    const idbReader = createIdbReader({
        DB_NAME,
        STORE,
        UPDATED_FIELD,
        UPDATED_INDEX,
        withDb,
        OFFLINE_MODE_ENABLED,
    });

    const localViewer = initLocalViewer({
        UPDATED_FIELD,
        POLL_MS,
        idbReader,
        remoteApi,
        elements,
    });

    // Les deux flux possibles renvoient des badges : on les traite pareil.
    lwOn('checkin.badges.server.lastly-modified.fetched', remoteApi.handleFetched);
    lwOn('checkin.badges.initial.fetched', remoteApi.handleFetched);

    // ------------------ Init sequence ------------------
    (async function init() {
        try {
            localViewer.setupInstantHooks(EVENT_ID);
            remoteApi.fetchRemoteBadges(lwDispatch); // kick off remote right away
            await localViewer.refresh({ forceRender: true });
            localViewer.startPolling();
            appendSimLog({ status: 'success', message: 'Simulator ready' });

            // Installe l'interrupteur Offline
            const offline = installOfflineSwitch({
                onChange: async (isOffline) => {
                    if (!isOffline) {
                        // De retour en ligne : relancer un pull remote et rafraîchir la vue locale
                        try { remoteApi.fetchRemoteBadges(lwDispatch); } catch { }
                        try { await localViewer.refresh({ forceRender: true }); } catch { }
                    }
                }
            });

        } catch (err) {
            elements.dbStatusEl && (elements.dbStatusEl.textContent = 'error');
            elements.dbStatusEl && (elements.dbStatusEl.className = 'badge rounded-pill text-bg-danger');
            elements.noticeEl && (elements.noticeEl.innerHTML = `<span class="text-danger fw-semibold">Init failed:</span> ${err?.message || err}`);
            appendSimLog({ status: 'danger', message: `Init failed: ${err?.message || err}` });
        }
    })();
}

// --- OFFLINE switch installer ----------------------------------------------
function installOfflineSwitch({ onChange } = {}) {
    // Essaie de trouver une toolbar existante ; sinon, injecte près du bouton Play
    const playBtn = document.querySelector('#simulator-play-btn');
    const host = document.getElementById('sim-toolbar')
        || (playBtn ? playBtn.parentElement : null)
        || document.body;

    const input = document.getElementById('simOfflineToggle');

    // Patchers réseau (fetch  XHR)
    const setOffline = (offline) => {

        window.SIMULATOR_OFFLINE = !!offline;

        const onlineIconEl = document.querySelector('.device-card__dot');

        // fetch
        if (!window.__origFetch) window.__origFetch = window.fetch;
        if (window.SIMULATOR_OFFLINE) {
            window.fetch = (...args) => Promise.reject(new TypeError('NetworkError (simulator offline)'));
            onlineIconEl.classList.remove('online');
        } else {
            if (window.__origFetch) window.fetch = window.__origFetch;
            onlineIconEl.classList.add('online');
        }

        // XHR
        const P = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
        if (P && typeof P.send === 'function') {
            if (!window.__origXHRSend) window.__origXHRSend = P.send;
            P.send = function (...args) {
                if (window.SIMULATOR_OFFLINE) {
                    try { this.onerror && this.onerror(new ProgressEvent('error')); } catch { }
                    try { this.dispatchEvent && this.dispatchEvent(new Event('error')); } catch { }
                    return;
                }
                return window.__origXHRSend.apply(this, args);
            };
        }

        // Broadcast aux modules (remote/local/…)
        try {
            window.dispatchEvent(new CustomEvent('simulator:offline:change', { detail: { offline: window.SIMULATOR_OFFLINE } }));
        } catch { }

        // Log
        appendSimLog({
            status: window.SIMULATOR_OFFLINE ? 'warning' : 'success',
            message: window.SIMULATOR_OFFLINE ? 'Simulator set to OFFLINE' : 'Simulator back ONLINE',
            type: 'SIMULATOR'
        });

        onChange && onChange(window.SIMULATOR_OFFLINE);
    };

    input.addEventListener('change', (e) => setOffline(e.target.checked));
    // Applique l'état mémorisé au chargement
    if (saved) setOffline(true);

    return { setOffline, input };
}

